import * as React from 'react'
import { type GenerationType } from './GenerationTypeSelector'
import { type GenerationMode } from './GenerationModeSelector'
import { type AspectOption, aspectOptions } from '../dimension_management/DimensionOptions'
import { generateFromUrl, generateWithControl, type GenerationResult } from '../imageEditingApi'
import { useLoading } from '../editor/loading_state/useLoading'


export type ImageFileType = 'image/jpeg' | 'image/jpg' | 'image/png'

const allowedFileTypes: ImageFileType[] = ['image/jpeg', 'image/jpg', 'image/png']
const maxFileSize = 5 * 1024 * 1024 // 5MB
const aiPlaceholderImage = '/images/create_with_ai_placeholder_1.jpeg'

// Minimum resolution requirements for structure-based generation
const minWidth = 1200
const minHeight = 628

export interface AiGenerationState {
    articleUrls: string
    customPrompt: string
    referenceImage: File | null
    error: string | null
    generationType: GenerationType
    selectedDimension: AspectOption
    isGenerating: boolean
    imageCanvasRef?: React.RefObject<HTMLCanvasElement>
    generationMode: GenerationMode
    isImageSuitableForStructure: boolean
    referenceImageDimensions: { width: number; height: number } | null
}

export interface AiGenerationHandlers {
    handleArticleUrlsChange: (value: string) => void
    handleCustomPromptChange: (value: string) => void
    handleReferenceImageSelect: (file: File) => void
    handleGenerationTypeChange: (type: GenerationType) => void
    handleDimensionChange: (option: AspectOption) => void
    handleGenerate: () => Promise<void>
    clearError: () => void
    reset: () => void
    onGenerationModeChange: (mode: GenerationMode) => void
}

export type UseAiGenerationReturn = AiGenerationState & AiGenerationHandlers

interface UseAiGenerationProps {
    imageCanvasRef?: React.RefObject<HTMLCanvasElement>
    onGenerateSuccess?: (result: GenerationResult) => void
    onGenerateError?: (error: Error) => void
}

export const useAiGeneration = ({
    imageCanvasRef,
    onGenerateSuccess,
    onGenerateError
}: UseAiGenerationProps): UseAiGenerationReturn => {
    // State for form inputs
    const [articleUrls, setArticleUrls] = React.useState('')
    const [customPrompt, setCustomPrompt] = React.useState('')
    const [referenceImage, setReferenceImage] = React.useState<File | null>(null)
    const [error, setError] = React.useState<string | null>(null)
    const [generationType, setGenerationType] = React.useState<GenerationType>('landingPage')
    const [selectedDimension, setSelectedDimension] = React.useState<AspectOption>(aspectOptions[0])
    const [generationMode, setGenerationMode] = React.useState<GenerationMode>('style')
    const [isImageSuitableForStructure, setIsImageSuitableForStructure] = React.useState(false)
    const [referenceImageDimensions, setReferenceImageDimensions] = React.useState<{ width: number; height: number } | null>(null)

    // Replace isGenerating state with useLoading hook
    const { startLoading, stopLoading, isLoading } = useLoading('ai-generation')

    // Memoized handlers
    const handleArticleUrlsChange = React.useCallback((value: string) => {
        setArticleUrls(value)
        setError(null)
    }, [])

    const handleCustomPromptChange = React.useCallback((value: string) => {
        setCustomPrompt(value)
        setError(null)
    }, [])

    const handleReferenceImageSelect = React.useCallback((file: File) => {
        // Validate file type
        if (!allowedFileTypes.includes(file.type as ImageFileType)) {
            setError('Please upload only JPG, JPEG or PNG files')
            return
        }

        // Validate file size
        if (file.size > maxFileSize) {
            setError('File size must be less than 5MB')
            return
        }

        // Create an image object to check dimensions
        const img = new Image()
        const objectUrl = URL.createObjectURL(file)

        img.onload = () => {
            // Check if the image meets the minimum resolution requirements
            const isSuitable = img.width >= minWidth && img.height >= minHeight
            setIsImageSuitableForStructure(isSuitable)
            setReferenceImageDimensions({ width: img.width, height: img.height })

            // If the image is not suitable for structure mode and that's the current mode,
            // switch to style mode
            if (!isSuitable && generationMode === 'structure') {
                setGenerationMode('style')
                setError(`Image resolution (${img.width}x${img.height}) is too small for structure-based generation. Minimum required is ${minWidth}x${minHeight}.`)
            } else {
                setError(null)
            }

            URL.revokeObjectURL(objectUrl)
        }

        img.onerror = () => {
            setError('Failed to load image. Please try another file.')
            URL.revokeObjectURL(objectUrl)
        }

        img.src = objectUrl
        setReferenceImage(file)
    }, [generationMode])

    const handleGenerationTypeChange = React.useCallback((type: GenerationType) => {
        setGenerationType(type)
        setError(null)
    }, [])

    const onGenerationModeChange = React.useCallback((mode: GenerationMode) => {
        // If trying to switch to structure mode but image is not suitable
        if (mode === 'structure' && !isImageSuitableForStructure && referenceImage) {
            if (referenceImageDimensions) {
                setError(`Image resolution (${referenceImageDimensions.width}x${referenceImageDimensions.height}) is too small for structure-based generation. Minimum required is ${minWidth}x${minHeight}.`)
            } else {
                setError(`Image resolution is too small for structure-based generation. Minimum required is ${minWidth}x${minHeight}.`)
            }
            return
        }

        setGenerationMode(mode)
        setError(null)
    }, [isImageSuitableForStructure, referenceImage, referenceImageDimensions])

    const handleDimensionChange = React.useCallback((option: AspectOption) => {
        setSelectedDimension(option)
        // Update canvas dimensions to match the selected dimensions
        const img = new Image()
        img.onload = () => {
            // Calculate scaled dimensions to maintain aspect ratio
            const originalAspect = img.width / img.height
            const targetAspect = option.targetWidth / option.targetHeight
            const newWidth = originalAspect > targetAspect
                ? Math.round(option.targetHeight * originalAspect)
                : option.targetWidth
            const newHeight = originalAspect > targetAspect
                ? option.targetHeight
                : Math.round(option.targetWidth / originalAspect)
            // Update dimensions if canvas ref exists
            if (imageCanvasRef?.current) {
                const canvas = imageCanvasRef.current
                canvas.width = newWidth
                canvas.height = newHeight
                const ctx = canvas.getContext('2d')
                if (ctx) {
                    ctx.drawImage(img, 0, 0, newWidth, newHeight)
                }
            }
        }
        img.src = aiPlaceholderImage
        setError(null)
    }, [imageCanvasRef])

    const clearError = React.useCallback(() => {
        setError(null)
    }, [])

    // Reset function to clear all inputs
    const reset = React.useCallback(() => {
        setArticleUrls('')
        setCustomPrompt('')
        setReferenceImage(null)
        setError(null)
        setGenerationType('landingPage')
        setSelectedDimension(aspectOptions[0])
        setGenerationMode('style')
        setIsImageSuitableForStructure(false)
        setReferenceImageDimensions(null)
    }, [])

    // Reset only the prompt
    const resetPrompt = React.useCallback(() => {
        setCustomPrompt('')
    }, [])

    // Generation handler
    const handleGenerate = React.useCallback(async () => {
        try {
            startLoading('Generating AI image...', 1) // Priority 1 for image generation
            setError(null)

            let result: GenerationResult

            if (generationType === 'landingPage') {
                if (!articleUrls.trim()) {
                    setError('Please enter a valid URL')
                    return
                }

                result = await generateFromUrl({
                    url: articleUrls,
                    userPrompt: customPrompt,
                    aspectRatio: '1.91:1', // Always use 1.91:1 for article-based generation
                    output_format: 'png',
                    style_preset: 'photographic'
                })
            } else if (generationType === 'prompt') {
                if (!customPrompt) {
                    setError('Please enter a prompt')
                    return
                }

                result = await generateFromUrl({
                    url: '', // Empty URL since we're using isPromptOnly
                    userPrompt: customPrompt,
                    aspectRatio: '1.91:1', // Use standard aspect ratio for prompt-based generation
                    output_format: 'png',
                    style_preset: 'photographic',
                    isPromptOnly: true // Flag to indicate prompt-only generation
                })
            } else {
                // Reference image generation
                if (!referenceImage || !customPrompt.trim()) {
                    setError('Please upload an image and provide a prompt')
                    return
                }

                // Convert File to base64
                const reader = new FileReader()
                const base64Promise = new Promise<string>((resolve, reject) => {
                    reader.onload = () => {
                        const base64 = reader.result as string
                        resolve(base64.split(',')[1]) // Remove data URL prefix
                    }
                    reader.onerror = reject
                })
                reader.readAsDataURL(referenceImage)
                const base64Image = await base64Promise

                result = await generateWithControl({
                    image: base64Image,
                    prompt: customPrompt,
                    mode: generationMode,
                    // Common parameters
                    output_format: 'png',
                    style_preset: 'photographic',
                    // Mode-specific parameters
                    ...(generationMode === 'style'
                        ? {
                            // Always use 16:9 aspect ratio for style-based generation
                            aspect_ratio: '16:9',
                            fidelity: 0.7,         // For style mode
                            processTo1200x628: true // Process to 1200x628 for style mode
                        }
                        : {
                            control_strength: 0.7,  // For structure mode
                            processTo1200x628: true // Process to 1200x628 for structure mode as well
                        }
                    )
                })
            }

            onGenerateSuccess?.(result)
            resetPrompt() // Only reset the prompt after successful generation
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err)
            setError(errorMessage)
            onGenerateError?.(err instanceof Error ? err : new Error(errorMessage))
        } finally {
            stopLoading()
        }
    }, [
        articleUrls,
        customPrompt,
        referenceImage,
        generationType,
        generationMode,
        selectedDimension,
        onGenerateSuccess,
        onGenerateError,
        resetPrompt,
        startLoading,
        stopLoading
    ])

    return {
        // State
        articleUrls,
        customPrompt,
        referenceImage,
        error,
        generationType,
        selectedDimension,
        isGenerating: isLoading(), // Use isLoading() for backward compatibility
        imageCanvasRef,
        generationMode,
        isImageSuitableForStructure,
        referenceImageDimensions,

        // Handlers
        handleArticleUrlsChange,
        handleCustomPromptChange,
        handleReferenceImageSelect,
        handleGenerationTypeChange,
        handleDimensionChange,
        handleGenerate,
        clearError,
        reset,
        onGenerationModeChange
    }
}
